import { DateTime } from "luxon";
import type { FC } from "react";
import React from "react";

import { Button } from "@react/ui-primitives/button";
import { DrawerHeader, DrawerTitle } from "@react/ui-primitives/drawer";

import type { LogEntry } from "@apis/calorie-grid/models";

import { CATEGORY_META } from "../../../bootstrap/category.ts";
import { useConfig } from "../../../shared/contexts/config/useConfig.ts";
import { shapeManager } from "../../../shared/lib/ShapeManager.ts";
import { usePlacementGridActions } from "../hooks/usePlacementGridActions.ts";

function formatDay(date: Date): string {
  return DateTime.fromJSDate(date).setLocale("en-US").toFormat("MMM d");
}

function getCaloriesToSquaresLabel(
  totalCalories: number,
  squareKcal: number,
): string {
  const squares = shapeManager.squareCount(totalCalories, squareKcal);

  return `${totalCalories.toLocaleString()} kcal → ${squares} sq`;
}

function getTimeSpanLabel(logEntry: LogEntry): string {
  const cells = logEntry.cells ?? [];

  if (cells.length === 0) {
    return "Counted as crumbs only.";
  }

  const times = cells.map(cell => DateTime.fromISO(cell.date).toMillis());

  const first = formatDay(new Date(Math.min(...times)));

  const last = formatDay(new Date(Math.max(...times)));

  return first === last ? `Eaten on ${first}.` : `Spread ${first} → ${last}.`;
}

interface LogEntryDetailsDrawerContentProps {
  readonly logEntry: LogEntry;
  readonly onClose: () => void;
}

export const LogEntryDetailsDrawerContent: FC<
  LogEntryDetailsDrawerContentProps
> = ({ logEntry, onClose }) => {
  const { removeFromGrid } = usePlacementGridActions();

  const categoryColor = CATEGORY_META[logEntry.category].color;

  const { squareKcal } = useConfig();
  const caloriesToSquaresLabel = getCaloriesToSquaresLabel(
    logEntry.totalCalories,
    squareKcal,
  );

  const timeSpanLabel = getTimeSpanLabel(logEntry);

  const handleRemoveFromGrid = (): void => {
    removeFromGrid.mutate(logEntry, { onSuccess: onClose });
  };

  return (
    <>
      <DrawerHeader>
        <DrawerTitle className="flex items-center gap-2">
          <span
            className="size-3 rounded-full"
            style={{ backgroundColor: categoryColor }}
          />
          {logEntry.name}
        </DrawerTitle>
      </DrawerHeader>
      <div className="flex flex-col gap-4 px-4 pb-8">
        <p className="font-mono text-sm text-muted-foreground">
          {caloriesToSquaresLabel}
        </p>
        <p className="text-sm text-muted-foreground">{timeSpanLabel}</p>
        <div className="flex gap-2">
          <Button
            className="flex-1"
            disabled={removeFromGrid.isPending}
            onClick={handleRemoveFromGrid}
            variant="outline"
          >
            Back to queue
          </Button>
          <Button className="flex-1" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </>
  );
};
